import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Tag,
  TagLabel,
  Divider,
} from '@chakra-ui/react';
import { 
  FaBullhorn, 
  FaChevronRight, 
  FaTrophy,
  FaMagic,
  FaMedal,
  FaAd,
  FaKey,
  FaUsers,
  FaStar,
  FaGoogle,
  FaEye,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getReviews } from '../services/reviewService';
import { logger } from '../utils/logger';
import {
  getAdHistory,
  getTotalAds,
  getAdKeywords,
  formatAdCount,
  getAvgAdPosition,
  getTopAdKeywords,
  formatNumber,
} from '../utils/competitiveIntelHelpers';

// Google Ad Preview Component (Mini version for widget)
const MiniGoogleAdPreview = ({ ad }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const urlColor = useColorModeValue('green.700', 'green.400');
  const titleColor = useColorModeValue('blue.700', 'blue.400');
  const bodyColor = useColorModeValue('gray.600', 'gray.400');
  const sponsoredBg = useColorModeValue('yellow.100', 'yellow.900');
  
  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={3}
      shadow="sm"
      _hover={{ shadow: 'md', borderColor: 'blue.300' }}
      transition="all 0.2s"
    >
      {/* Sponsored label */}
      <HStack spacing={2} mb={2}>
        <Badge bg={sponsoredBg} color="gray.700" fontSize="2xs" px={1.5} py={0.5} borderRadius="sm">
          Sponsored
        </Badge>
      </HStack>
      
      {/* URL Line */}
      <HStack spacing={1} mb={1}>
        <Icon as={FaGoogle} color="gray.400" boxSize={2.5} />
        <Text fontSize="xs" color={urlColor} noOfLines={1}>
          {ad.url ? ad.url.replace(/^https?:\/\//, '').split('/')[0] : 'example.com'}
        </Text>
      </HStack>
      
      {/* Title */}
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color={titleColor}
        mb={1}
        noOfLines={1}
        _hover={{ textDecoration: 'underline' }}
        cursor="pointer"
      >
        {ad.title || 'Untitled Ad'}
      </Text>
      
      {/* Body */}
      <Text fontSize="xs" color={bodyColor} noOfLines={2} mb={2}>
        {ad.body || 'No description available'}
      </Text>
      
      {/* Business name */}
      {ad.businessName && (
        <Text fontSize="2xs" color="gray.500">
          <Icon as={FaUsers} mr={1} boxSize={2} /> {ad.businessName}
        </Text>
      )}
    </Box>
  );
};

// Top Advertiser Mini Card
const TopAdvertiserMiniCard = ({ advertiser, rank }) => {
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  
  const getRankIcon = (rank) => {
    switch(rank) {
      case 1: return { icon: FaTrophy, color: 'yellow.400' };
      case 2: return { icon: FaMedal, color: 'gray.400' };
      case 3: return { icon: FaMedal, color: 'orange.400' };
      default: return { icon: FaAd, color: 'blue.400' };
    }
  };
  
  const { icon, color } = getRankIcon(rank);
  
  return (
    <Box 
      p={3} 
      bg={statBg} 
      borderRadius="lg" 
      borderLeft="4px solid" 
      borderLeftColor={rank === 1 ? 'yellow.400' : rank === 2 ? 'gray.400' : 'orange.400'}
    >
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Icon as={icon} color={color} boxSize={4} />
          <Text fontWeight="bold" color={textColor} fontSize="sm" noOfLines={1} maxW="120px">
            {advertiser.name.split(' ').slice(0, 2).join(' ')}
          </Text>
        </HStack>
        <Badge colorScheme="green" fontSize="xs">
          #{advertiser.avgPosition.toFixed(1)}
        </Badge>
      </HStack>
      
      <HStack spacing={3} fontSize="xs" color="gray.500">
        <HStack spacing={1}>
          <Icon as={FaAd} boxSize={2.5} />
          <Text>{formatAdCount(advertiser.adCount)}</Text>
        </HStack>
        <HStack spacing={1}>
          <Icon as={FaKey} boxSize={2.5} />
          <Text>{advertiser.keywordCount}</Text>
        </HStack>
      </HStack>
    </Box>
  );
};

/**
 * AdIntelligenceWidget - Displays advertising intelligence
 * @param {string} variant - 'full' for expanded view (WIN section), 'compact' for smaller view (HOME section)
 */
const AdIntelligenceWidget = ({ variant = 'compact' }) => {
  const navigate = useNavigate();
  const [reviewsData, setReviewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const iconBg = useColorModeValue('gray.100', 'gray.800');
  const statCardBg = useColorModeValue('white', 'gray.800');
  const ctaBg = useColorModeValue('purple.50', 'purple.900');
  const ctaTextColor = useColorModeValue('purple.700', 'purple.200');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const reviewsResult = await getReviews();
        
        if (mounted) {
          setReviewsData(reviewsResult || []);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error fetching data for Ad Intelligence Widget:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Calculate comprehensive ad intelligence metrics
  const adMetrics = useMemo(() => {
    const businesses = Array.isArray(reviewsData) ? reviewsData : reviewsData.reviews || [];
    
    if (!businesses || businesses.length === 0) {
      return { 
        totalAdvertisers: 0,
        totalAds: 0,
        totalKeywords: 0,
        avgPosition: 0,
        topPositionRate: 0,
        marketCoverage: 0,
        topAdvertisers: [],
        topKeywords: [],
        exampleAds: [],
        businessesWithAds: [],
      };
    }

    // Filter businesses with ad history
    const businessesWithAds = businesses.filter(business => {
      const ads = getAdHistory(business);
      return ads.length > 0;
    });

    const totalAdvertisers = businessesWithAds.length;
    
    // Calculate aggregate statistics
    let totalAdsCount = 0;
    const allKeywords = new Set();
    let totalPositionSum = 0;
    let adsWithPosition = 0;
    let topPositionCount = 0;

    businessesWithAds.forEach(business => {
      totalAdsCount += getTotalAds(business);
      
      const keywords = getAdKeywords(business);
      keywords.forEach(kw => allKeywords.add(kw));
      
      const ads = getAdHistory(business);
      ads.forEach(ad => {
        if (ad.position) {
          totalPositionSum += ad.position;
          adsWithPosition++;
          if (ad.position <= 3) topPositionCount++;
        }
      });
    });

    const avgPosition = adsWithPosition > 0 ? totalPositionSum / adsWithPosition : 0;
    const topPositionRate = adsWithPosition > 0 ? (topPositionCount / adsWithPosition * 100) : 0;
    const marketCoverage = businesses.length > 0 ? ((businessesWithAds.length / businesses.length) * 100) : 0;

    // Top advertisers by ad count
    const topAdvertisers = [...businessesWithAds]
      .map(business => ({
        name: business.name,
        adCount: getTotalAds(business),
        keywordCount: getAdKeywords(business).length,
        avgPosition: getAvgAdPosition(business),
      }))
      .sort((a, b) => b.adCount - a.adCount)
      .slice(0, 3);

    // Top keywords
    const topKeywords = getTopAdKeywords(businessesWithAds, 6);

    // Collect example ads (get 4 unique ads from different businesses)
    const exampleAds = [];
    const seenBusinesses = new Set();
    
    for (const business of businessesWithAds) {
      if (exampleAds.length >= 4) break;
      if (seenBusinesses.has(business.name)) continue;
      
      const ads = getAdHistory(business);
      if (ads.length > 0) {
        const ad = ads[0];
        exampleAds.push({
          title: ad.title || 'Untitled Ad',
          body: ad.body || '',
          url: ad.url || '',
          businessName: business.name,
          position: ad.position,
          keywords: ad.keywords || [],
        });
        seenBusinesses.add(business.name);
      }
    }

    return {
      totalAdvertisers,
      totalAds: totalAdsCount,
      totalKeywords: allKeywords.size,
      avgPosition: avgPosition.toFixed(2),
      topPositionRate: topPositionRate.toFixed(1),
      marketCoverage: marketCoverage.toFixed(1),
      avgAdsPerBusiness: totalAdvertisers > 0 ? (totalAdsCount / totalAdvertisers).toFixed(1) : 0,
      topAdvertisers,
      topKeywords,
      exampleAds,
      businessesWithAds,
    };
  }, [reviewsData]);

  const handleViewAds = () => {
    navigate('/dashboards/ads-intelligence');
  };

  const handleGenerateAds = () => {
    navigate('/dashboards/ads-intelligence');
  };

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        minH={variant === 'full' ? '600px' : '400px'}
      >
        <Flex justify="center" align="center" h="100%">
          <Spinner size="lg" color="blue.500" thickness="3px" />
        </Flex>
      </Box>
    );
  }

  // Show empty state if no ad data
  if (adMetrics.totalAdvertisers === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        minH="400px"
        transition="all 0.3s"
        _hover={{
          shadow: 'md',
          borderColor: 'blue.200',
        }}
      >
        {/* Header */}
        <Box
          px={5}
          py={4}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg={headerBg}
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                bg={iconBg}
                borderRadius="lg"
                justify="center"
                align="center"
              >
                <Icon as={FaBullhorn} color="blue.500" boxSize={5} />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg" color={textColor}>
                  Google Ads
                </Text>
                <Text fontSize="xs" color={subtitleColor}>
                  Competitor advertising analysis
                </Text>
              </VStack>
            </HStack>
          </Flex>
        </Box>

        {/* Empty State */}
        <Flex 
          direction="column" 
          align="center" 
          justify="center" 
          p={8}
          minH="300px"
        >
          <Flex
            w="80px"
            h="80px"
            bg="gray.100"
            borderRadius="full"
            justify="center"
            align="center"
            mb={4}
          >
            <Icon as={FaBullhorn} boxSize={10} color="gray.400" />
          </Flex>
          <Text fontWeight="medium" color={subtitleColor} textAlign="center" fontSize="lg">
            No advertising data available yet
          </Text>
          <Text fontSize="sm" color={subtitleColor} textAlign="center" mt={2} maxW="400px">
            Ad intelligence will appear once businesses are enriched with ad history data
          </Text>
        </Flex>
      </Box>
    );
  }

  // COMPACT VARIANT - Simplified view for HOME section
  if (variant === 'compact') {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        transition="all 0.3s"
        _hover={{
          shadow: 'lg',
          borderColor: 'blue.200',
        }}
      >
        {/* Header */}
        <Box
          px={5}
          py={4}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg={headerBg}
        >
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={3}>
              <Flex
                w="44px"
                h="44px"
                bg={iconBg}
                borderRadius="lg"
                justify="center"
                align="center"
              >
                <Icon as={FaBullhorn} color="blue.500" boxSize={5} />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="1.4em" color={textColor} textTransform="uppercase" fontStyle="italic">
                  Google Ads
                </Text>
                <Text fontSize="xs" color={subtitleColor}>
                  Competitor advertising analysis • {adMetrics.totalAdvertisers} active advertisers
                </Text>
              </VStack>
            </HStack>
            <HStack spacing={2}>
              <Button
                size="md"
                colorScheme="purple"
                leftIcon={<FaMagic />}
                onClick={handleGenerateAds}
                shadow="md"
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
              >
                Generate Ads
              </Button>
              <Button
                size="md"
                colorScheme="blue"
                variant="outline"
                rightIcon={<FaChevronRight />}
                onClick={handleViewAds}
              >
                Full Dashboard
              </Button>
            </HStack>
          </Flex>
        </Box>

        {/* Top Competitors Section - Horizontal Layout */}
        <Box px={5} py={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={4} />
                <Text fontWeight="bold" color={textColor} fontSize="md">
                  Top Competitors
                </Text>
              </HStack>
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                By Ad Volume
              </Badge>
            </HStack>
            
            {/* Horizontal competitor cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              {adMetrics.topAdvertisers.map((advertiser, index) => (
                <TopAdvertiserMiniCard 
                  key={index} 
                  advertiser={advertiser} 
                  rank={index + 1} 
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Competitor Ads Section */}
        {adMetrics.exampleAds && adMetrics.exampleAds.length > 0 && (
          <Box px={5} pb={4}>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Icon as={FaGoogle} color="blue.500" boxSize={4} />
                  <Text fontWeight="bold" color={textColor} fontSize="md">
                    Competitor Ads
                  </Text>
                </HStack>
                <Button 
                  size="xs" 
                  variant="ghost" 
                  colorScheme="blue" 
                  rightIcon={<FaChevronRight />}
                  onClick={handleViewAds}
                >
                  View All
                </Button>
              </HStack>
              
              {/* 3 Competitor Ad Previews */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {adMetrics.exampleAds.slice(0, 3).map((ad, index) => (
                  <MiniGoogleAdPreview key={index} ad={ad} />
                ))}
              </SimpleGrid>
            </VStack>
          </Box>
        )}

        {/* Bottom CTA Section */}
        <Box
          px={5}
          py={4}
          bg={ctaBg}
          borderTopWidth="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <VStack align="start" spacing={0}>
              <HStack spacing={2}>
                <Icon as={FaMagic} color="purple.500" boxSize={4} />
                <Text fontWeight="bold" color={ctaTextColor}>
                  AI Ad Generator
                </Text>
              </HStack>
              <Text fontSize="sm" color={subtitleColor}>
                Create optimized ads based on competitor analysis
              </Text>
            </VStack>
            <Button
              colorScheme="purple"
              size="lg"
              leftIcon={<FaMagic />}
              onClick={handleGenerateAds}
              shadow="md"
              _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
            >
              Generate Winning Ads
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  }

  // FULL VARIANT - Comprehensive view for WIN section
  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      transition="all 0.3s"
      _hover={{
        shadow: 'lg',
        borderColor: 'blue.200',
      }}
    >
      {/* Header */}
      <Box
        px={5}
        py={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerBg}
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Flex
              w="44px"
              h="44px"
              bg={iconBg}
              borderRadius="lg"
              justify="center"
              align="center"
            >
              <Icon as={FaBullhorn} color="blue.500" boxSize={5} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="1.4em" color={textColor} textTransform="uppercase" fontStyle="italic">
                Google Ads
              </Text>
              <Text fontSize="xs" color={subtitleColor}>
                Competitor advertising analysis • {adMetrics.totalAdvertisers} active advertisers
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            <Button
              size="md"
              colorScheme="purple"
              leftIcon={<FaMagic />}
              onClick={handleGenerateAds}
              shadow="md"
              _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
            >
              Generate Ads
            </Button>
            <Button
              size="md"
              colorScheme="blue"
              variant="outline"
              rightIcon={<FaChevronRight />}
              onClick={handleViewAds}
            >
              Full Dashboard
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Hero Stats Section - Bubble Cards */}
      <Box px={5} py={4}>
        <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={3}>
          {/* Advertisers */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="blue.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaUsers} color="blue.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {adMetrics.totalAdvertisers}
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Advertisers</Text>
          </Box>

          {/* Total Ads */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="purple.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaAd} color="purple.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="purple.600">
              {formatNumber(adMetrics.totalAds)}
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Total Ads</Text>
          </Box>

          {/* Keywords */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="green.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaKey} color="green.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {formatNumber(adMetrics.totalKeywords)}
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Keywords</Text>
          </Box>

          {/* Avg Position */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="orange.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaTrophy} color="orange.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
              #{adMetrics.avgPosition}
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Avg Position</Text>
          </Box>

          {/* Top 3 Rate */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="yellow.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaStar} color="yellow.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
              {adMetrics.topPositionRate}%
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Top 3 Rate</Text>
          </Box>

          {/* Market Coverage */}
          <Box
            bg={statCardBg}
            borderRadius="xl"
            p={3}
            shadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            textAlign="center"
            _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            <Flex justify="center" mb={2}>
              <Flex w="32px" h="32px" bg="cyan.50" borderRadius="full" justify="center" align="center">
                <Icon as={FaEye} color="cyan.500" boxSize={3.5} />
              </Flex>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color="cyan.600">
              {adMetrics.marketCoverage}%
            </Text>
            <Text fontSize="xs" color={subtitleColor}>Coverage</Text>
          </Box>
        </SimpleGrid>
      </Box>

      <Divider />

      {/* Two Column Layout: Top Competitors & Ad Gallery */}
      <Box px={5} py={4}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Left Column: Top Competitors */}
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={4} />
                <Text fontWeight="bold" color={textColor} fontSize="md">
                  Top Competitors
                </Text>
              </HStack>
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                By Ad Volume
              </Badge>
            </HStack>
            
            <VStack spacing={3} align="stretch">
              {adMetrics.topAdvertisers.map((advertiser, index) => (
                <TopAdvertiserMiniCard 
                  key={index} 
                  advertiser={advertiser} 
                  rank={index + 1} 
                />
              ))}
            </VStack>

            {/* Top Keywords Section */}
            <Box mt={2}>
              <HStack spacing={2} mb={3}>
                <Icon as={FaKey} color="green.500" boxSize={3.5} />
                <Text fontWeight="bold" color={textColor} fontSize="sm">
                  Top Keywords
                </Text>
              </HStack>
              <Flex flexWrap="wrap" gap={2}>
                {adMetrics.topKeywords.slice(0, 6).map((kw, index) => (
                  <Tag 
                    key={index} 
                    size="sm" 
                    colorScheme={index < 2 ? 'blue' : index < 4 ? 'green' : 'gray'}
                    variant="subtle"
                  >
                    <TagLabel>{kw.keyword}</TagLabel>
                    <Badge ml={1} fontSize="2xs" colorScheme="purple" variant="solid">
                      {formatAdCount(kw.adCount)}
                    </Badge>
                  </Tag>
                ))}
              </Flex>
            </Box>
          </VStack>

          {/* Right Column: Ad Gallery Preview */}
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaGoogle} color="blue.500" boxSize={4} />
                <Text fontWeight="bold" color={textColor} fontSize="md">
                  Competitor Ads
                </Text>
              </HStack>
              <Button 
                size="xs" 
                variant="ghost" 
                colorScheme="blue" 
                rightIcon={<FaChevronRight />}
                onClick={handleViewAds}
              >
                View All
              </Button>
            </HStack>
            
            <SimpleGrid columns={2} spacing={3}>
              {adMetrics.exampleAds.slice(0, 4).map((ad, index) => (
                <MiniGoogleAdPreview key={index} ad={ad} />
              ))}
            </SimpleGrid>
          </VStack>
        </SimpleGrid>
      </Box>

      {/* Bottom CTA Section */}
      <Box
        px={5}
        py={4}
        bg={ctaBg}
        borderTopWidth="1px"
        borderColor={borderColor}
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <VStack align="start" spacing={0}>
            <HStack spacing={2}>
              <Icon as={FaMagic} color="purple.500" boxSize={4} />
              <Text fontWeight="bold" color={ctaTextColor}>
                AI Ad Generator
              </Text>
            </HStack>
            <Text fontSize="sm" color={subtitleColor}>
              Create optimized ads based on competitor analysis
            </Text>
          </VStack>
          <Button
            colorScheme="purple"
            size="lg"
            leftIcon={<FaMagic />}
            onClick={handleGenerateAds}
            shadow="md"
            _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
          >
            Generate Winning Ads
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default AdIntelligenceWidget;
